// Frame Agent panel: direct a new take from the frame under the playhead.
// Form -> POST frame-direct -> plan card -> (target frame) -> approve. Generation then runs through
// the existing ops, so the pending take shows in the existing shot-in-progress placeholder.
// A 202 ({ jobId, room, proposalId }) has no plan yet: the panel waits for that proposal to carry one in
// project.proposals (a Record by id), which VideoDirector's 3 s doc poll keeps fresh (the Director has no socket helper).

import React, { useEffect, useState } from "react";
import i18n from "i18next";
import { LuImage, LuX } from "react-icons/lu";
import VideoThumb from "../../../timeline/IdeaTimeline/components/VideoThumb";
import { ModelPick, OverlayPanel, effectiveModel } from "./DirectorPanels";

const t = (key, opts) => i18n.t(`FileModal.APIProviders.VideoGeneration.Director.FrameAgent.${key}`, opts);
const td = (key, opts) => i18n.t(`FileModal.APIProviders.VideoGeneration.Director.${key}`, opts);
const tv = (key) => i18n.t(`FileModal.APIProviders.VideoGeneration.${key}`);

const btn = { fontSize: 12, padding: "6px 14px", borderRadius: "4px" };
const label = { fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px" };
const kr = (n) => i18n.t("FileModal.APIProviders.VideoGeneration.Director.Models.PriceShort", { n });

export default function DirectorFrameAgent({
  anchor, // { segmentId, frame, url, seekSec, time, range? }
  proposals, // project.proposals: Record<id, Proposal>
  models,
  model,
  imageModel,
  onModel,
  onCall, // (sub, body) => Promise<response | null>; null = failed (the parent shows the error)
  onClose,
  error,
  onDismissError,
}) {
  const [intent, setIntent] = useState("");
  const [point, setPoint] = useState(null);
  const [stage, setStage] = useState("form"); // form | loading | plan
  const [res, setRes] = useState(null); // { proposalId, plan, estCredits }
  const [editFrame, setEditFrame] = useState(null); // inline prompt edit for the target frame
  const [pending, setPending] = useState(false);

  const live = Object.values(proposals || {}).find((p) => p.id === res?.proposalId);
  const plan = res?.plan;
  const endframe = plan?.strategy === "endframe_then_continue";
  // The 202 fallback carries no estimate: price it from the loaded unit prices.
  const takeUnit = models?.video?.[effectiveModel(models, "video", model)];
  const imageUnit = models?.image?.[effectiveModel(models, "image", imageModel)];
  const withImage = endframe && typeof imageUnit === "number";
  const est =
    res?.estCredits ??
    (typeof takeUnit === "number" && plan && plan.strategy !== "clarify"
      ? {
          take: takeUnit,
          ...(withImage ? { image: imageUnit } : {}),
          total: takeUnit + (withImage ? imageUnit : 0),
        }
      : undefined);
  const stillStatus = live?.stillStatus;
  const stillUrl = live?.stillUrl;
  const stillBusy = stillStatus === "pending";
  const stillReady = stillStatus === "ready" && !!stillUrl;
  const stillPrompt = live?.stillPrompt || plan?.endFrameImagePrompt || "";
  const fromStart = anchor.range?.startFrame === 0; // mw cannot cut before the first frame
  const range = anchor.range && !fromStart ? anchor.range : null;

  // 202: resolve from the project doc once the proposal we were promised carries its plan.
  useEffect(() => {
    if (stage !== "loading" || !live?.plan) return;
    setRes({ proposalId: live.id, plan: live.plan });
    setStage("plan");
  }, [stage, live]);

  const post = async (text) => {
    setPending(true);
    const out = await onCall("frame-direct", {
      segmentId: anchor.segmentId,
      frame: anchor.frame,
      ...(point ? { point } : {}),
      ...(range ? { range } : {}),
      intent: text,
      ...(model ? { model } : {}),
    });
    setPending(false);
    if (!out) return;
    if (out.plan) {
      setRes(out);
      setStage("plan");
    } else {
      setRes({ proposalId: out.proposalId }); // 202 { jobId, room, proposalId }
      setStage("loading");
    }
  };
  const run = async (sub, body) => {
    setPending(true);
    const out = await onCall(`frame-direct/${res.proposalId}/${sub}`, body);
    setPending(false);
    return out;
  };
  const createStill = async (prompt) => {
    if (await run("still", prompt ? { prompt } : {})) setEditFrame(null); // 202: the doc poll brings the result
  };
  const approve = async () => {
    if (await run("approve", model ? { model } : {})) onClose();
  };
  const dismiss = async () => {
    await run("dismiss");
    onClose();
  };
  const pickPoint = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const c = (v) => Math.min(1, Math.max(0, v));
    setPoint({ x: c((e.clientX - r.left) / r.width), y: c((e.clientY - r.top) / r.height) });
  };

  const thumb = (
    <div style={{ position: "relative", display: "inline-block", maxWidth: 320, width: "100%" }}>
      <div
        data-testid="fa-thumb"
        role="presentation"
        style={{ cursor: stage === "form" ? "crosshair" : "default" }}
        onClick={stage === "form" ? pickPoint : undefined}
      >
        <VideoThumb
          url={anchor.url}
          seekSec={anchor.seekSec}
          className="add-video-modal__director-frame-thumb"
        />
      </div>
      {point && (
        <span
          data-testid="fa-point"
          style={{
            position: "absolute",
            left: `${point.x * 100}%`,
            top: `${point.y * 100}%`,
            width: 12,
            height: 12,
            marginLeft: -6,
            marginTop: -6,
            borderRadius: 6,
            border: "2px solid #fff",
            background: "#7c3aed",
            boxShadow: "0 0 0 1px #7c3aed",
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  );

  let body;
  let actions;
  if (stage === "plan" && plan?.strategy === "clarify") {
    body = (
      <>
        <div style={{ fontSize: 13, color: "#334155", marginBottom: 10 }}>{plan.clarify?.question}</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {(plan.clarify?.options || []).map((o) => (
            <button
              key={o}
              type="button"
              className="add-video-modal__ai-chip add-video-modal__director-model"
              disabled={pending}
              onClick={async () => {
                await run("dismiss"); // the clarify proposal is superseded
                post(`${intent.trim()} — ${o}`);
              }}
            >
              {o}
            </button>
          ))}
        </div>
      </>
    );
    actions = (
      <>
        <button type="button" className="add-video-modal__btn add-video-modal__btn--secondary" style={btn} onClick={() => setStage("form")}>
          {t("Change")}
        </button>
        <button type="button" className="add-video-modal__btn add-video-modal__btn--ghost" style={btn} onClick={dismiss}>
          {tv("Cancel")}
        </button>
      </>
    );
  } else if (stage === "plan") {
    const frameCost = est?.image;
    body = (
      <>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
          {t(`S_${plan.strategy}`)}
        </div>
        <div style={{ fontSize: 13, color: "#475569", marginBottom: 12 }}>{plan.rationale}</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          {thumb}
          {endframe && stillBusy && (
            <div role="status" style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
              <span className="add-video-modal__scene-spinner" />
            </div>
          )}
          {endframe && stillReady && !stillBusy && (
            <img
              data-testid="fa-still"
              src={stillUrl}
              alt=""
              className="add-video-modal__director-frame-thumb"
              style={{ maxWidth: 320 }}
            />
          )}
        </div>
        {endframe && stillStatus === "failed" && (
          <div role="alert" style={{ fontSize: 13, color: "#b91c1c", marginBottom: 12 }}>
            {live.stillError && live.stillError !== "generic" ? `${t("StillFailed")} (${live.stillError})` : t("StillFailed")}
          </div>
        )}
        {endframe && stillReady && !stillBusy && (
          <div style={{ marginBottom: 12 }}>
            {editFrame === null ? (
              <button
                type="button"
                className="add-video-modal__btn add-video-modal__btn--secondary"
                style={{ ...btn, padding: "4px 12px", display: "flex", gap: 4, alignItems: "center" }}
                disabled={pending || stillBusy}
                onClick={() => setEditFrame(stillPrompt)}
              >
                <LuImage size={12} />
                {typeof frameCost === "number" ? `${td("Regenerate")} · ${kr(frameCost)}` : td("Regenerate")}
              </button>
            ) : (
              <>
                <textarea
                  className="add-video-modal__scene-prompt-textarea"
                  style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid #e2e8f0", borderRadius: 6 }}
                  rows={3}
                  aria-label={t("FramePrompt")}
                  value={editFrame}
                  onChange={(e) => setEditFrame(e.target.value)}
                />
                <button
                  type="button"
                  className="add-video-modal__btn add-video-modal__btn--secondary"
                  style={{ ...btn, marginTop: 6 }}
                  disabled={pending || stillBusy || !editFrame.trim()}
                  onClick={() => createStill(editFrame.trim())}
                >
                  {typeof frameCost === "number" ? `${t("Update")} · ${kr(frameCost)}` : t("Update")}
                </button>
              </>
            )}
          </div>
        )}
        {est && (
          <dl style={{ margin: 0, fontSize: 13, color: "#334155" }} aria-label={t("CostTotal")}>
            {[
              ["CostVideo", est.take],
              ...(typeof est.image === "number" ? [["CostFrame", est.image]] : []),
            ].map(([k, n]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between" }}>
                <dt style={{ fontWeight: 400 }}>{t(k)}</dt>
                <dd style={{ margin: 0 }}>{kr(n)}</dd>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
              <dt>{t("CostTotal")}</dt>
              <dd style={{ margin: 0 }}>{kr(est.total)}</dd>
            </div>
          </dl>
        )}
      </>
    );
    const approveCost = est && (stillReady ? est.take : est.total);
    actions = (
      <>
        {endframe && !stillReady ? (
          <button
            type="button"
            className="add-video-modal__btn add-video-modal__btn--primary"
            style={btn}
            disabled={pending || stillBusy}
            onClick={() => createStill()}
          >
            {(() => {
              const name = stillStatus === "failed" ? td("Retry") : t("CreateFrame");
              return typeof est?.image === "number" ? `${name} · ${kr(est.image)}` : name;
            })()}
          </button>
        ) : (
          <button
            type="button"
            className="add-video-modal__btn add-video-modal__btn--primary"
            style={btn}
            disabled={pending || stillBusy}
            onClick={approve}
          >
            {typeof approveCost === "number" ? `${td("Approve")} · ${kr(approveCost)}` : td("Approve")}
          </button>
        )}
        <button type="button" className="add-video-modal__btn add-video-modal__btn--secondary" style={btn} disabled={pending} onClick={() => setStage("form")}>
          {t("Change")}
        </button>
        <button type="button" className="add-video-modal__btn add-video-modal__btn--ghost" style={btn} disabled={pending} onClick={dismiss}>
          {tv("Cancel")}
        </button>
      </>
    );
  } else {
    const loading = stage === "loading";
    body = (
      <>
        <div style={label}>{t("FrameAt", { time: anchor.time })}</div>
        {thumb}
        {fromStart && (
          <div style={{ margin: "6px 0 0", fontSize: 12, color: "#64748b" }}>{t("RangeFromStart")}</div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "6px 0 12px", fontSize: 12, color: "#64748b" }}>
          <span>{t("MarkCaption")}</span>
          {point && (
            <button
              type="button"
              className="add-video-modal__ai-chip"
              aria-label={t("ClearMark")}
              title={t("ClearMark")}
              onClick={() => setPoint(null)}
            >
              <LuX size={12} />
            </button>
          )}
        </div>
        <textarea
          className="add-video-modal__scene-prompt-textarea"
          style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid #e2e8f0", borderRadius: 6, marginBottom: 10 }}
          rows={4}
          maxLength={500}
          aria-label={t("IntentPlaceholder")}
          placeholder={t("IntentPlaceholder")}
          value={intent}
          disabled={loading}
          onChange={(e) => setIntent(e.target.value)}
        />
        <ModelPick kind="video" value={model} onChange={onModel} models={models} />
        {loading && (
          <div role="status" style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 10, fontSize: 13 }}>
            <span className="add-video-modal__scene-spinner" />
            {t("Planning")}
          </div>
        )}
      </>
    );
    actions = (
      <>
        <button
          type="button"
          className="add-video-modal__btn add-video-modal__btn--primary"
          style={btn}
          disabled={pending || loading || !intent.trim()}
          onClick={() => post(intent.trim())}
        >
          {t("Plan")}
        </button>
        <button type="button" className="add-video-modal__btn add-video-modal__btn--ghost" style={btn} onClick={onClose}>
          {tv("Cancel")}
        </button>
      </>
    );
  }

  return (
    <OverlayPanel
      title={t("Title")}
      onClose={onClose}
      error={error}
      onDismissError={onDismissError}
      actions={<div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>{actions}</div>}
    >
      {body}
    </OverlayPanel>
  );
}
